import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBSpinner,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
} from 'mdb-react-ui-kit';
import { generateClient } from 'aws-amplify/api';
import { useTranslation } from 'react-i18next';
import { captureError } from '../../utils/sentryHelpers';
import { getLocalizedText } from '../../utils/localizedText';
import { compareMediaSeries, getLocalized, mediathekImageUrl } from '../../utils/mediathek';
import {
  DELETE_MEDIA_SERIES,
  LIST_INDUSTRIES,
  LIST_MEDIA_SERIES,
  MEDIA_ITEM_IDS_BY_MEDIA_SERIES,
  fetchAllPages,
} from './mediathekGraphql';
import { ConfirmModal, ErrorList, FlashToast, StatusBadge, TypeBadge, useFlashToast } from './MediathekShared';

const client = generateClient();
const COMPONENT = 'MediaSeriesList';

/** Item ids/status of a series: first page from the list query, the rest via mediaItemsByMediaSeries. */
async function collectSeriesItems(series) {
  const first = series.items?.items || [];
  const nextToken = series.items?.nextToken;
  if (!nextToken) return first;
  const all = [...first];
  let token = nextToken;
  do {
    const response = await client.graphql({
      query: MEDIA_ITEM_IDS_BY_MEDIA_SERIES,
      variables: { mediaSeriesId: series.id, limit: 1000, nextToken: token },
    });
    const connection = response?.data?.mediaItemsByMediaSeries;
    all.push(...(connection?.items || []));
    token = connection?.nextToken || null;
  } while (token);
  return all;
}

export default function MediaSeriesList() {
  const { t, i18n } = useTranslation();
  const [seriesList, setSeriesList] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useFlashToast();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setErrors([]);
    try {
      const [series, industryList] = await Promise.all([
        fetchAllPages(client, LIST_MEDIA_SERIES, {}, 'listMediaSeries'),
        fetchAllPages(client, LIST_INDUSTRIES, {}, 'listIndustries').catch((error) => {
          console.error('Error fetching industries:', error);
          captureError(error, { feature: 'mediathek', operation: 'fetch-industries', tags: { component: COMPONENT } });
          return [];
        }),
      ]);
      const withCounts = await Promise.all(series.map(async (entry) => {
        const items = await collectSeriesItems(entry);
        return {
          ...entry,
          itemCount: items.length,
          publishedCount: items.filter((item) => item?.status === 'published').length,
        };
      }));
      setSeriesList(withCounts.sort(compareMediaSeries));
      setIndustries(industryList);
    } catch (error) {
      console.error('Error fetching media series:', error);
      captureError(error, { feature: 'mediathek', operation: 'fetch-series-list', tags: { component: COMPONENT } });
      setErrors(['The series could not be loaded']);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const industryName = useMemo(() => {
    const byId = Object.fromEntries(industries.map((industry) => [industry.id, industry]));
    return (industryId) => {
      if (!industryId) return t('All industries');
      const industry = byId[industryId];
      return industry ? getLocalizedText(industry.name) || industry.handle : industryId;
    };
  }, [industries, t]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      // Re-check against the backend: items may have been added since the list was loaded
      const check = await client.graphql({
        query: MEDIA_ITEM_IDS_BY_MEDIA_SERIES,
        variables: { mediaSeriesId: deleteTarget.id, limit: 1 },
      });
      const connection = check?.data?.mediaItemsByMediaSeries;
      if ((connection?.items || []).length > 0 || connection?.nextToken) {
        setDeleteTarget({ ...deleteTarget, itemCount: Math.max(deleteTarget.itemCount || 0, 1) });
        return;
      }
      await client.graphql({ query: DELETE_MEDIA_SERIES, variables: { input: { id: deleteTarget.id } } });
      setSeriesList((prev) => prev.filter((entry) => entry.id !== deleteTarget.id));
      setDeleteTarget(null);
      setToast('Series deleted');
    } catch (error) {
      console.error('Error deleting media series:', error);
      captureError(error, {
        feature: 'mediathek',
        operation: 'delete-series',
        context: { seriesId: deleteTarget.id },
        tags: { component: COMPONENT },
      });
      setErrors([error?.errors?.[0]?.message || error?.message || 'The series could not be deleted']);
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteBlocked = (deleteTarget?.itemCount || 0) > 0;

  return (
    <>
      <MDBCard>
        <MDBCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h5 className="mb-0">{t('Media library')}: {t('Series & formats')}</h5>
            <div className="d-flex gap-2">
              <Link to="/admin/mediathek/items">
                <MDBBtn color="secondary" outline>
                  <MDBIcon fas icon="list" className="me-2" />
                  {t('All items')}
                </MDBBtn>
              </Link>
              <Link to="/admin/mediathek/series/create">
                <MDBBtn color="primary">
                  <MDBIcon fas icon="plus" className="me-2" />
                  {t('New series')}
                </MDBBtn>
              </Link>
            </div>
          </div>

          <ErrorList errors={errors} />

          {isLoading ? (
            <div className="text-center py-5">
              <MDBSpinner color="primary" />
            </div>
          ) : seriesList.length === 0 ? (
            <p className="text-muted mb-0">{t('No series yet.')}</p>
          ) : (
            <MDBTable hover responsive align="middle">
              <MDBTableHead>
                <tr>
                  <th style={{ width: '70px' }}></th>
                  <th>{t('Title')}</th>
                  <th>{t('Type')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Industry')}</th>
                  <th className="text-end">{t('Items')}</th>
                  <th className="text-end">{t('Sort key')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {seriesList.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      {entry.coverImage?.fileName ? (
                        <img
                          src={mediathekImageUrl(entry.coverImage.fileName)}
                          alt=""
                          className="rounded border"
                          style={{ width: '56px', height: '56px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="rounded border d-flex align-items-center justify-content-center text-muted" style={{ width: '56px', height: '56px' }}>
                          <MDBIcon fas icon={entry.type === 'video' ? 'video' : 'microphone'} />
                        </div>
                      )}
                    </td>
                    <td>
                      <Link to={`/admin/mediathek/series/${entry.id}`} className="fw-bold">
                        {getLocalized(entry.title, i18n.language) || t('(untitled)')}
                      </Link>
                      {entry.slug && <div className="text-muted small"><code>/mediathek/{entry.slug}</code></div>}
                    </td>
                    <td><TypeBadge type={entry.type} /></td>
                    <td><StatusBadge status={entry.status} /></td>
                    <td>{industryName(entry.industryId)}</td>
                    <td className="text-end" title={t('Published / total')}>
                      {entry.publishedCount} / {entry.itemCount}
                    </td>
                    <td className="text-end">{entry.sortKey ?? '-'}</td>
                    <td className="text-nowrap">
                      <Link to={`/admin/mediathek/series/${entry.id}`}>
                        <MDBBtn color="link" size="sm" title={t('Edit')}>
                          <MDBIcon fas icon="edit" />
                        </MDBBtn>
                      </Link>
                      <Link to={`/admin/mediathek/series/${entry.id}/items/create`}>
                        <MDBBtn color="link" size="sm" title={t('New item')}>
                          <MDBIcon fas icon="plus" />
                        </MDBBtn>
                      </Link>
                      <MDBBtn color="link" size="sm" title={t('Delete')} onClick={() => setDeleteTarget(entry)}>
                        <MDBIcon fas icon="trash" className="text-danger" />
                      </MDBBtn>
                    </td>
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
          )}
        </MDBCardBody>
      </MDBCard>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title={t('Delete series')}
        message={deleteBlocked
          ? t('This series still has {{itemCount}} items. Delete them or move them to another series first.', { itemCount: deleteTarget?.itemCount })
          : t('Are you sure you want to delete the series "{{title}}"?', { title: getLocalized(deleteTarget?.title, i18n.language) })}
        onConfirm={deleteBlocked ? undefined : handleDelete}
        onClose={() => setDeleteTarget(null)}
        busy={isDeleting}
      />
      <FlashToast message={toast} onClose={() => setToast(null)} />
    </>
  );
}
